import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getApiListings, Listing } from "../../api/listings-client";
import Footer from '../Footer';
import MainHeader from '../MainHeader';
import ListingResultsView from '../ListingResultsView';
import { SharedSearchBar } from '../SearchBar';
import SearchOverlay from '../SearchOverlay';

// City coordinates data
const CITY_DATA = {
  'sydney': {
    name: 'Sydney',
    state: 'NSW',
    latitude: -33.8688, 
    longitude: 151.2093,
    radius: 25, // km
    description: 'Find affordable sharehouses in Sydney, NSW. Browse rooms in Australia\'s largest city, from beachside suburbs to inner-city living.'
  },
  'melbourne': {
    name: 'Melbourne',
    state: 'VIC',
    latitude: -37.8136, 
    longitude: 144.9631,
    radius: 25,
    description: 'Discover Melbourne\'s vibrant sharehouse scene. Find your perfect room in Australia\'s cultural capital, from trendy Brunswick to leafy South Yarra.'
  },
  'brisbane': {
    name: 'Brisbane',
    state: 'QLD',
    latitude: -27.4698, 
    longitude: 153.0251,
    radius: 25,
    description: 'Explore Brisbane sharehouses and find your ideal room in Queensland\'s sunny capital. From riverside apartments to suburban homes.'
  },
  'perth': {
    name: 'Perth',
    state: 'WA',
    latitude: -31.9505, 
    longitude: 115.8605,
    radius: 25,
    description: 'Browse Perth sharehouses and rooms for rent. Find affordable accommodation in Western Australia\'s beautiful capital city.'
  },
  'goldcoast': {
    name: 'Gold Coast',
    state: 'QLD',
    latitude: -28.0167, 
    longitude: 153.4000,
    radius: 25,
    description: 'Find sharehouses on the Gold Coast. Discover rooms near beaches, universities and entertainment districts in this popular Queensland destination.'
  },
  'adelaide': {
    name: 'Adelaide',
    state: 'SA',
    latitude: -34.9285, 
    longitude: 138.6007,
    radius: 25,
    description: 'Explore Adelaide sharehouses and find your next room in South Australia\'s capital. From beachside suburbs to the city center.'
  },
  'canberra': {
    name: 'Canberra',
    state: 'ACT',
    latitude: -35.2809, 
    longitude: 149.1300,
    radius: 25,
    description: 'Discover Canberra sharehouses. Find rooms in Australia\'s capital city, ideal for students, professionals and public servants.'
  },
  'newcastle': {
    name: 'Newcastle',
    state: 'NSW',
    latitude: -32.9283, 
    longitude: 151.7817,
    radius: 25,
    description: 'Browse Newcastle sharehouses and find your perfect room in this coastal NSW city. Great options for students and beach lovers.'
  },
  'hobart': {
    name: 'Hobart',
    state: 'TAS',
    latitude: -42.8821, 
    longitude: 147.3272,
    radius: 25,
    description: 'Find sharehouses in Hobart. Discover rooms in Tasmania\'s capital city with its beautiful harbor, historic buildings and vibrant culture.'
  },
  'darwin': {
    name: 'Darwin',
    state: 'NT',
    latitude: -12.4634, 
    longitude: 130.8456,
    radius: 25,
    description: 'Explore Darwin sharehouses. Find rooms in the Northern Territory\'s tropical capital city with its relaxed lifestyle and multicultural community.'
  }
};

const PAGE_SIZE = 9;

const CityDirectoryPage = ({ cityKey }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchState, setSearchState] = useState({
    listings: [],
    pagination: null,
    snapshot: undefined
  });

  // Get city data based on the cityKey prop
  const cityData = useMemo(() => {
    return CITY_DATA[cityKey] || null;
  }, [cityKey]);

  // Parse page from URL query params
  const currentPage = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseInt(searchParams.get('page') || '1');
  }, [location.search]);

  // Handle search bar focus for mobile
  const handleSearchFocus = () => {
    if (window.innerWidth < 640) {
      setIsSearchOpen(true);
    }
  };

  // Handle search items change
  const handleItemsChange = (newItems) => {
    setSelectedItems(newItems);
  };

  // Fetch listings using coordinates
  useEffect(() => {
    async function fetchListings() {
      if (!cityData) return;

      try {
        setIsLoading(true);
        
        const { latitude, longitude, radius } = cityData;
        
        const response = await getApiListings({
          latitude,
          longitude,
          distance: radius,
          page: currentPage,
          limit: PAGE_SIZE,
          snapshot: searchState.snapshot
        });

        setSearchState({
          listings: response.listings || [],
          pagination: response.pagination || null,
          snapshot: response.pagination?.snapshotTimestamp
        });
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchListings();
  }, [cityData, currentPage]);

  // Handle page change
  const handlePageChange = (page) => {
    setIsLoading(true);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', page.toString());
    
    // Update URL with new page parameter
    window.history.pushState(
      null, 
      '', 
      `${location.pathname}?${searchParams.toString()}`
    );
    
    // Force a re-render with the new page
    window.dispatchEvent(new Event('popstate'));
  };

  // If invalid city, show error or redirect
  if (!cityData) {
    return (
      <div className="min-h-screen bg-white">
        <MainHeader />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold text-center mb-4">City Not Found</h1>
          <p className="text-center">Sorry, we couldn't find information for this city.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <MainHeader />
      
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="py-6">
            <SharedSearchBar
              selectedItems={selectedItems}
              onItemsChange={handleItemsChange}
              onFocus={handleSearchFocus}
            />
          </div>
        </div>
      </div>

      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        selectedItems={selectedItems}
        onItemsChange={handleItemsChange}
      />

      {/* City Header with SEO Content */}
      <div className="bg-gradient-to-r from-[#24ccf2]/5 to-[#2476f2]/5 py-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Sharehouses in {cityData.name}, {cityData.state}
          </h1>
          <p className="text-gray-600 max-w-3xl mb-4">
            {cityData.description}
          </p>
        </div>
      </div>

      <div className="pt-6 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <ListingResultsView
            key={`${cityKey}-page-${currentPage}`}
            localities={[]}
            listings={searchState.listings}
            isLoading={isLoading}
            pageSize={PAGE_SIZE}
            pagination={searchState.pagination ?? undefined}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CityDirectoryPage;